// Composant provider pour initialiser Google Analytics
import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeAnalytics, updateConsentSettings, disableAnalytics } from '../../lib/analytics';

interface AnalyticsContextType {
  isInitialized: boolean;
  hasConsent: boolean;
  updateConsent: (consent: boolean) => void;
  disable: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    // Vérifier le consentement stocké localement
    const storedConsent = localStorage.getItem('analytics_consent');
    const consent = storedConsent === 'true';
    
    setHasConsent(consent);

    // Initialiser Analytics seulement si le consentement est accordé
    if (consent) {
      try {
        initializeAnalytics();
        setIsInitialized(true);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation d\'Analytics:', error);
      }
    }
  }, []);

  const updateConsent = (consent: boolean) => {
    setHasConsent(consent);
    localStorage.setItem('analytics_consent', consent.toString());
    
    if (consent && !isInitialized) {
      // Initialiser Analytics si pas encore fait
      try {
        initializeAnalytics();
        setIsInitialized(true);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation d\'Analytics:', error);
      }
    }
    
    // Mettre à jour les paramètres de consentement
    updateConsentSettings(consent);
  };

  const disable = () => {
    disableAnalytics();
    setHasConsent(false);
    setIsInitialized(false);
    localStorage.setItem('analytics_consent', 'false');
  };

  return (
    <AnalyticsContext.Provider value={{
      isInitialized,
      hasConsent,
      updateConsent,
      disable
    }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
};